/** Built-in procedural textures for the Texture effect. Drawn on a canvas
 *  tile instead of shipping image assets — a tile is a few lines of 2D ctx
 *  and stays crisp at any scale, and there is nothing to load/cache/host.
 *  Ink is painted in `color` over a transparent tile, so the element's own
 *  fill shows through and blend modes behave. */

export type TexturePatternId =
  | "paper"
  | "grain"
  | "canvas"
  | "concrete"
  | "scratches"
  | "dots"
  | "halftone"
  | "crosshatch"
  | "lines"
  | "diagonal"
  | "grid"
  | "checker"
  | "waves";

/** Labels are intentionally not run through i18n — they are short material
 *  names shown next to a visual swatch. */
export const TEXTURE_PATTERNS: readonly {
  id: TexturePatternId;
  label: string;
}[] = [
  { id: "paper", label: "Paper" },
  { id: "grain", label: "Grain" },
  { id: "canvas", label: "Canvas" },
  { id: "concrete", label: "Concrete" },
  { id: "scratches", label: "Scratches" },
  { id: "dots", label: "Dots" },
  { id: "halftone", label: "Halftone" },
  { id: "crosshatch", label: "Crosshatch" },
  { id: "lines", label: "Lines" },
  { id: "diagonal", label: "Diagonal" },
  { id: "grid", label: "Grid" },
  { id: "checker", label: "Checker" },
  { id: "waves", label: "Waves" },
];

const PATTERN_IDS = new Set<string>(TEXTURE_PATTERNS.map((p) => p.id));

export const isTexturePatternId = (value: unknown): value is TexturePatternId =>
  typeof value === "string" && PATTERN_IDS.has(value);

/** deterministic PRNG — tiles are cached, but an evicted tile must redraw
 *  identically or the shape would visibly change on re-render */
const rng = (seed: number) => () => {
  seed = (seed * 1664525 + 1013904223) >>> 0;
  return (seed >>> 8) / 0x1000000;
};

/** Every draw fn is authored against a 256px tile and multiplies lengths by
 *  `u`, so the same pattern renders at any tile size (incl. UI swatches). */
const drawers: Record<
  TexturePatternId,
  (ctx: CanvasRenderingContext2D, size: number, color: string) => void
> = {
  paper: (ctx, size, color) => {
    const rand = rng(7);
    ctx.fillStyle = color;
    const u = size / 256;
    for (let i = 0; i < size * size * 0.3; i++) {
      ctx.globalAlpha = 0.04 + rand() * 0.12;
      ctx.fillRect(
        rand() * size,
        rand() * size,
        Math.max(1, u),
        Math.max(1, u),
      );
    }
    // faint long fibres
    ctx.globalAlpha = 0.06;
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(1, u);
    for (let i = 0; i < 40; i++) {
      const x = rand() * size;
      const y = rand() * size;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + (rand() - 0.5) * 40 * u, y + (rand() - 0.5) * 40 * u);
      ctx.stroke();
    }
  },

  grain: (ctx, size, color) => {
    const rand = rng(11);
    ctx.fillStyle = color;
    for (let i = 0; i < size * size * 0.55; i++) {
      ctx.globalAlpha = 0.08 + rand() * 0.45;
      ctx.fillRect(rand() * size, rand() * size, 1, 1);
    }
  },

  canvas: (ctx, size, color) => {
    const u = size / 256;
    const step = 8 * u;
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(1, 3 * u);
    for (let i = 0, y = 0; y < size; i++, y += step) {
      ctx.globalAlpha = i % 2 ? 0.14 : 0.24;
      ctx.beginPath();
      ctx.moveTo(0, y + step / 2);
      ctx.lineTo(size, y + step / 2);
      ctx.stroke();
    }
    for (let i = 0, x = 0; x < size; i++, x += step) {
      ctx.globalAlpha = i % 2 ? 0.24 : 0.14;
      ctx.beginPath();
      ctx.moveTo(x + step / 2, 0);
      ctx.lineTo(x + step / 2, size);
      ctx.stroke();
    }
  },

  concrete: (ctx, size, color) => {
    const rand = rng(23);
    ctx.fillStyle = color;
    const u = size / 256;
    for (let i = 0; i < 260; i++) {
      ctx.globalAlpha = 0.03 + rand() * 0.1;
      ctx.beginPath();
      ctx.arc(
        rand() * size,
        rand() * size,
        (2 + rand() * 16) * u,
        0,
        Math.PI * 2,
      );
      ctx.fill();
    }
    for (let i = 0; i < size * size * 0.15; i++) {
      ctx.globalAlpha = 0.05 + rand() * 0.15;
      ctx.fillRect(rand() * size, rand() * size, 1, 1);
    }
  },

  scratches: (ctx, size, color) => {
    const rand = rng(31);
    ctx.strokeStyle = color;
    const u = size / 256;
    for (let i = 0; i < 70; i++) {
      const x = rand() * size;
      const y = rand() * size;
      const angle = rand() * Math.PI * 2;
      const len = (10 + rand() * 70) * u;
      ctx.globalAlpha = 0.08 + rand() * 0.3;
      ctx.lineWidth = Math.max(0.5, rand() * 1.6 * u);
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + Math.cos(angle) * len, y + Math.sin(angle) * len);
      ctx.stroke();
    }
  },

  dots: (ctx, size, color) => {
    const u = size / 256;
    const step = 32 * u;
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.5;
    for (let row = 0, y = step / 2; y < size; row++, y += step) {
      // staggered rows — still seamless, the row offset repeats every 2 rows
      const shift = row % 2 ? step / 2 : 0;
      for (let x = step / 2; x < size + step; x += step) {
        ctx.beginPath();
        ctx.arc(((x + shift) % size) + 0.001, y, 4 * u, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  },

  halftone: (ctx, size, color) => {
    const u = size / 256;
    const step = 24 * u;
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.55;
    for (let y = step / 2; y < size; y += step) {
      for (let x = step / 2; x < size; x += step) {
        // radius modulated by a full-period sine so the tile stays seamless
        const k =
          (Math.sin((x / size) * Math.PI * 2) *
            Math.sin((y / size) * Math.PI * 2) +
            1) /
          2;
        ctx.beginPath();
        ctx.arc(x, y, (1.5 + k * 7) * u, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  },

  crosshatch: (ctx, size, color) => {
    const u = size / 256;
    const step = 16 * u;
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(1, 1.5 * u);
    ctx.globalAlpha = 0.35;
    ctx.beginPath();
    for (let i = -size; i < size * 2; i += step) {
      ctx.moveTo(i, -size);
      ctx.lineTo(i + size * 2, size);
      ctx.moveTo(i, size * 2);
      ctx.lineTo(i + size * 2, 0);
    }
    ctx.stroke();
  },

  lines: (ctx, size, color) => {
    const u = size / 256;
    const step = 16 * u;
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(1, 2 * u);
    ctx.globalAlpha = 0.35;
    ctx.beginPath();
    for (let y = step / 2; y < size; y += step) {
      ctx.moveTo(0, y);
      ctx.lineTo(size, y);
    }
    ctx.stroke();
  },

  diagonal: (ctx, size, color) => {
    const u = size / 256;
    const step = 16 * u;
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(1, 4 * u);
    ctx.globalAlpha = 0.3;
    ctx.beginPath();
    for (let i = -size; i < size * 2; i += step) {
      ctx.moveTo(i, -size);
      ctx.lineTo(i + size * 2, size);
    }
    ctx.stroke();
  },

  grid: (ctx, size, color) => {
    const u = size / 256;
    const step = 32 * u;
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(1, 1.5 * u);
    ctx.globalAlpha = 0.35;
    ctx.beginPath();
    for (let p = 0; p < size; p += step) {
      ctx.moveTo(p, 0);
      ctx.lineTo(p, size);
      ctx.moveTo(0, p);
      ctx.lineTo(size, p);
    }
    ctx.stroke();
  },

  checker: (ctx, size, color) => {
    const cell = size / 8;
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.3;
    for (let row = 0; row < 8; row++) {
      for (let col = row % 2; col < 8; col += 2) {
        ctx.fillRect(col * cell, row * cell, cell, cell);
      }
    }
  },

  waves: (ctx, size, color) => {
    const u = size / 256;
    const step = 24 * u;
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(1, 2 * u);
    ctx.globalAlpha = 0.35;
    for (let y = 0; y < size; y += step) {
      ctx.beginPath();
      for (let x = 0; x <= size; x += 2) {
        // one full period across the tile → seamless left/right
        const wy = y + Math.sin((x / size) * Math.PI * 2) * 6 * u;
        if (x === 0) {
          ctx.moveTo(x, wy);
        } else {
          ctx.lineTo(x, wy);
        }
      }
      ctx.stroke();
    }
  },
};

/** Draws a pattern into `ctx` filling a `size`×`size` square. Used both for
 *  the cached render tile and for the panel's swatches. */
export const renderTexturePattern = (
  ctx: CanvasRenderingContext2D,
  size: number,
  id: TexturePatternId,
  color: string,
) => {
  ctx.save();
  ctx.lineCap = "round";
  drawers[id](ctx, size, color);
  ctx.restore();
};

const TILE_SIZE = 256;
const tileCache = new Map<string, HTMLCanvasElement>();

/** Cached 256px tile for a (pattern, color) pair. Returns null off-DOM.
 *  ponytail: a tile is built synchronously on first use (~ms for the grain-y
 *  ones); dragging the tint picker builds one per colour step. If that ever
 *  reads as jank, debounce the tint commit rather than caching harder. */
export const getTexturePatternTile = (
  id: TexturePatternId,
  color: string,
): HTMLCanvasElement | null => {
  const key = `${id}:${color}`;
  const cached = tileCache.get(key);
  if (cached) {
    return cached;
  }
  const tile = document.createElement("canvas");
  tile.width = TILE_SIZE;
  tile.height = TILE_SIZE;
  const ctx = tile.getContext("2d");
  if (!ctx) {
    return null;
  }
  renderTexturePattern(ctx, TILE_SIZE, id, color);
  // 256×256×4B per tile — keep the ceiling at a few MB
  if (tileCache.size >= 24) {
    // ponytail: drop oldest insertion — a real LRU buys nothing for a cache
    // keyed by (13 patterns × colors actually in use)
    tileCache.delete(tileCache.keys().next().value!);
  }
  tileCache.set(key, tile);
  return tile;
};
